import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Timer } from "../../model/Timer.model";
import { getAllTimers } from "../../api/Timer.api";
import DateOrMonthFilter from "./DateOrMonthFilter";
// import dayjs from "dayjs";
import {getTimersByMonth} from '../../api/Timer.api'

const ManagerAttendancePage: React.FC = () => {
  const [viewMode, setViewMode] = useState<"users" | "projects">("users");
  const [loading, setLoading] = useState(false);
  const [timers, setTimers] = useState<Timer[]>([]);
  const [filteredTimers, setFilteredTimers] = useState<Timer[]>([]);
  const [groupedTimersByUser, setGroupedTimersByUser] = useState<{
    [userName: string]: Timer[];
  }>({});
  const [groupedByProject, setGroupedByProject] = useState<{
    [projectName: string]: Timer[];
  }>({});
  const [popupData, setPopupData] = useState<any | null>(null);
  const [popupMode, setPopupMode] = useState<"project" | "user" | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getAllTimers();
        const timersData = res.data;
        setTimers(timersData);

        setFilteredTimers(timersData);
        groupTimersByUser(timersData);
        groupByProject(timersData);
      } catch (err) {
        console.error("שגיאה בטעינת טיימרים:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFilter = (
    startDate?: string,
    endDate?: string,
    month?: string
  ) => {
    let filtered = timers;

    if (startDate && endDate) {
      filtered = timers.filter((t) => {});
    } else if (month) {
      filtered = timers.filter((t) => {});
    }

    setFilteredTimers(filtered);
    groupTimersByUser(filtered);
    groupByProject(filtered);
  };

  const groupTimersByUser = (timers: Timer[]) => {
    const grouped: { [userName: string]: Timer[] } = {};
    timers.forEach((timer) => {
      if (!grouped[timer.userName]) grouped[timer.userName] = [];
      grouped[timer.userName].push(timer);
    });
    setGroupedTimersByUser(grouped);
  };

  const groupByProject = (timers: Timer[]) => {
    const grouped: { [projectName: string]: Timer[] } = {};
    timers.forEach((timer) => {
      if (!grouped[timer.projectName]) grouped[timer.projectName] = [];
      grouped[timer.projectName].push(timer);
    });
    setGroupedByProject(grouped);
  };

  const formatDuration = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, "0");
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const calcTotalDuration = (timers: Timer[]) => {
    let total = 0;
    timers.forEach((t) => {
      const [h, m, s] = t.duration?.split(":").map(Number) || [0, 0, 0];
      total += h * 3600 + m * 60 + s;
    });
    return total;
  };

  const getProjectDetails = (projectName: string) => {
    const relevantTimers = groupedByProject[projectName];
    const byUser: { [userName: string]: number } = {};
    relevantTimers.forEach((t) => {
      const [h, m, s] = t.duration?.split(":").map(Number) || [0, 0, 0];
      byUser[t.userName] = (byUser[t.userName] || 0) + h * 3600 + m * 60 + s;
    });
    return Object.entries(byUser).map(([name, seconds]) => ({
      name,
      duration: formatDuration(seconds),
    }));
  };

  const getUserDetails = (userName: string) => {
    const relevantTimers = groupedTimersByUser[userName];
    const byProject: { [projectName: string]: number } = {};
    relevantTimers.forEach((t) => {
      const [h, m, s] = t.duration?.split(":").map(Number) || [0, 0, 0];
      byProject[t.projectName] =
        (byProject[t.projectName] || 0) + h * 3600 + m * 60 + s;
    });
    return Object.entries(byProject).map(([name, seconds]) => ({
      name,
      duration: formatDuration(seconds),
    }));
  };

  return (
    <Box sx={{ padding: 4, fontFamily: "CustomFont" , direction: "rtl"}}>
      <Typography
        variant="h4"
        align="center"
        sx={{ fontWeight: 700, color: "#002046", mb: 3 }}
      >
        דוח נוכחות
      </Typography>

      <DateOrMonthFilter onFilter={handleFilter} />

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4 , }}>
        <Button
          variant={viewMode === "users" ? "contained" : "outlined"}
          onClick={() => setViewMode("users")}
        >
          לפי עובדים
        </Button>
        <Button
          variant={viewMode === "projects" ? "contained" : "outlined"}
          onClick={() => setViewMode("projects")}
        >
          לפי פרויקטים
        </Button>
      </Box>
      {loading && <div style={{width:"100%", display:"flex", justifyContent:"center", margin:"10px"}}>
        <CircularProgress />
      </div>}

      {viewMode === "users" &&
        Object.entries(groupedTimersByUser).map(([userName, list]) => (
          <Paper key={userName} sx={{ padding: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>{userName}</Typography>
              <Typography>
                סה"כ שעות: {formatDuration(calcTotalDuration(list))}
              </Typography>
              <Typography>
                מספר פרויקטים: {new Set(list.map((t) => t.projectName)).size}
              </Typography>
              <Button
                onClick={() => {
                  setPopupData(getUserDetails(userName));
                  setPopupMode("user");
                }}
              >
                הצג פרויקטים
              </Button>
            </Box>
          </Paper>
        ))}

      {viewMode === "projects" &&
        Object.entries(groupedByProject).map(([projectName, list]) => (
          <Paper key={projectName} sx={{ padding: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>{projectName}</Typography>
              <Typography>
                סה"כ שעות: {formatDuration(calcTotalDuration(list))}
              </Typography>
              <Typography>
                מספר עובדות: {new Set(list.map((t) => t.userName)).size}
              </Typography>
              <Button
                onClick={() => {
                  setPopupData(getProjectDetails(projectName));
                  setPopupMode("project");
                }}
              >
                הצג עובדות
              </Button>
            </Box>
          </Paper>
        ))}

      <Dialog open={!!popupData} onClose={() => setPopupData(null)}>
        <DialogTitle>
          {popupMode === "project" ? "עובדות בפרויקט" : "פרויקטים של עובדת"}
          <IconButton
            onClick={() => setPopupData(null)}
            sx={{ position: "absolute", left: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {popupData?.map((item: any) => (
            <DialogContentText key={item.name} sx={{ mb: 1 }}>
              • {item.name} – {item.duration}
            </DialogContentText>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManagerAttendancePage;
